import React, { useRef, useState, useCallback, useLayoutEffect } from 'react';
import { Divider } from 'antd';
import {
    PlusOutlined,
    CalendarOutlined,
    AppstoreOutlined,
    DollarOutlined,
    InboxOutlined,
    EditOutlined,
    DeleteOutlined,
    LeftOutlined,
    RightOutlined,
    AlignLeftOutlined,
} from '@ant-design/icons';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import MultipleButton from '@/components/ui/MultipleButton';
import { SliderTab, ScrollWrapper, ScrollArrow } from '@/components/ui/SliderTab';
import ClampedMarkdownDescription from '@/components/ui/ClampedMarkdownDescription';
import PhaseDescriptionCollapsible from '@/components/module/projects/milestone_tab/PhaseDescriptionCollapsible';
import PhaseDescriptionAttachments from '@/components/module/projects/milestone_tab/PhaseDescriptionAttachments';
import StatusBadge from '@/components/ui/StatusBadge';
import { asCurrency } from '@/helpers/format';
import {
    formatDate,
    getMilestoneDescriptionText,
    getMilestoneDeliverablesList,
    getMilestoneDeliverableCompletionPercent,
    getMilestoneDisplayLabel,
    findSubmissionForMilestone,
    getPhaseDescriptionText,
    getPhaseAttachmentList,
    getMilestoneAttachmentList,
    getPhaseDisplayTitle,
    linkifyPlainUrlsForMarkdown,
    linkifyPlainUrlsInHtml,
} from '@hooks/projects/projectHelpers';
import MilestoneDeliverablesPanel from '@/components/module/projects/milestone_tab/MilestoneDeliverablesPanel';
import { resolveDescriptionMentionsForDisplay } from '@utils/richTextMentions';
import { useAuthStore } from '@/stores/auth.store';
import {
    BoardPageTitle,
    BoardPageSubtitle,
    BoardPillTabRow,
    BoardPillTab,
    BoardPillTabLabel,
    BoardUnderlineTabRow,
    BoardUnderlineTabInner,
    BoardUnderlineTab,
    BoardUnderlineTabLine,
    BoardUnderlineTabPrimaryLabel,
    BoardUnderlineTabPercent,
    ContentCard,
    ContentCardRow,
    MutedText,
    EmptyStateBlock,
    EmptyStateIcon,
    EmptyStateTitle,
    EmptyStateDescription,
    PanelSectionLabel,
} from '@/pages/inside/tenant/style';

function MilestoneMetaRow({ dueDate, deliverableCount }) {
    return (
        <Container $display="flex" $flexWrap="wrap" $gap="20px" $alignItems="center" style={{ marginTop: 12 }}>
            {dueDate && (
                <MutedText style={{ fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 8, margin: 0 }}>
                    <CalendarOutlined />
                    Due: {dueDate}
                </MutedText>
            )}
            <MutedText style={{ fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 8, margin: 0 }}>
                <AppstoreOutlined />
                {deliverableCount} Deliverable{deliverableCount !== 1 ? 's' : ''}
            </MutedText>
        </Container>
    );
}

/**
 * Phases + Milestones layout driven by GET /projects/:id payload.
 */
export default function MilestonesTabLayout({
    usePhases = true,
    useMilestones = true,
    isPhasesOnlyMode = false,
    project,
    phases,
    phaseTabs,
    selectedPhaseId,
    setSelectedPhaseId,
    selectedPhase,
    milestonesForPhase,
    selectedMilestoneId,
    setSelectedMilestoneId,
    selectedMilestone,
    canCreateMilestone,
    createMilestoneDisabled,
    createMilestoneDisabledTooltip,
    canEditMilestone,
    canDeleteMilestone,
    canCreatePhase,
    canEditPhase,
    canDeletePhase,
    canViewPayments,
    openPhaseModal,
    openEditPhaseModal,
    onDeletePhase,
    deletePhasePending,
    openMilestoneModal,
    openEditMilestoneModal,
    onAddMilestoneDeliverable,
    onToggleMilestoneDeliverable,
    onEditMilestoneDeliverable,
    onDeleteMilestoneDeliverable,
    createDeliverablePending,
    updateDeliverablePending,
    editDeliverablePending,
    deleteDeliverablePending,
    onRemoveMilestoneAttachment,
    updateMilestonePending,
    onDeleteMilestone,
    deleteMilestonePending,
    onMilestoneLinkClick,
    onPhaseLinkClick,
}) {
    const phaseDescriptionHtml = selectedPhase
        ? resolveDescriptionMentionsForDisplay(
            linkifyPlainUrlsForMarkdown(getPhaseDescriptionText(selectedPhase)),
            selectedPhase?.descriptionMentions,
        )
        : '';
    const phaseDescriptionLinked = linkifyPlainUrlsInHtml(phaseDescriptionHtml);
    const phaseAttachments = selectedPhase ? getPhaseAttachmentList(selectedPhase) : [];
    const hasPhaseDescriptionBody =
        (phaseDescriptionHtml && phaseDescriptionHtml.trim().length > 0) || phaseAttachments.length > 0;
    const milestoneAttachments = selectedMilestone ? getMilestoneAttachmentList(selectedMilestone) : [];
    const milestoneDeliverables = selectedMilestone ? getMilestoneDeliverablesList(selectedMilestone) : [];
    const submission = selectedMilestone && project ? findSubmissionForMilestone(project, selectedMilestone.id) : null;
    const paymentStatus = submission?.status ?? null;
    const budget = project?.budget;

    const user = useAuthStore((state) => state.user);
    const role = user?.role;

    const trackRef = useRef(null);
    const [showLeftScroll, setShowLeftScroll] = useState(false);
    const [showRightScroll, setShowRightScroll] = useState(false);

    const updateScrollButtons = useCallback(() => {
        if (!trackRef.current) return;
        const { scrollLeft, scrollWidth, clientWidth } = trackRef.current;
        setShowLeftScroll(scrollLeft > 0);
        setShowRightScroll(Math.ceil(scrollLeft + clientWidth) < scrollWidth);
    }, []);

    useLayoutEffect(() => {
        updateScrollButtons();
        window.addEventListener('resize', updateScrollButtons);
        return () => window.removeEventListener('resize', updateScrollButtons);
    }, [updateScrollButtons, milestonesForPhase]);

    const scrollBy = (amount) => {
        if (trackRef.current) {
            trackRef.current.scrollBy({ left: amount, behavior: 'smooth' });
            setTimeout(updateScrollButtons, 300);
        }
    };

    if (!usePhases && !useMilestones) {
        return (
            <Container style={{ padding: '40px 24px', textAlign: 'center' }}>
                <EmptyStateBlock>
                    <EmptyStateIcon>
                        <InboxOutlined />
                    </EmptyStateIcon>
                    <EmptyStateTitle>Features Disabled</EmptyStateTitle>
                    <EmptyStateDescription>
                        Phases and Milestones are disabled according to the capabilities of this project type.
                    </EmptyStateDescription>
                </EmptyStateBlock>
            </Container>
        );
    }

    return (
        <Container
            $display="flex"
            $flexDirection="column"
            $flex="1"
            $minHeight="0"
            // Keep this tab scrollable, but pull scrollbar to the page edge.
            $overflow="auto"
        >
            {/* Keep scrollbar flush right; pad content inside only. */}
            <div style={{ width: '100%', paddingRight: 24, boxSizing: 'border-box', marginBottom: 24 }}>
                {/* Phases */}
                {usePhases && (
                    <>
                        <Container
                            $display="flex"
                            $justifyContent="space-between"
                            $alignItems="flex-start"
                            $gap="16px"
                            $flexWrap="wrap"
                            style={{ flexShrink: 0 }}
                        >
                            <div>
                                <BoardPageTitle>Phases</BoardPageTitle>
                                <BoardPageSubtitle>Create phases to group milestones and track delivery.</BoardPageSubtitle>
                            </div>

                            <Button
                                type="button"
                                $primary
                                $blackOnLight
                                $size="13px"
                                $padding="8px 16px"
                                icon={<PlusOutlined />}
                                permissionDenied={!canCreatePhase}
                                onClick={openPhaseModal}
                                isHidden={role === 'Client'}
                            >
                                Create Phase
                            </Button>
                        </Container>

                        {phaseTabs.length > 0 ? (
                            <Container $marginTop="20px" $marginBottom="16px" $flexShrink="0">
                                <SliderTab
                                    items={phaseTabs.map((p) => ({
                                        key: p.id,
                                        label: getPhaseDisplayTitle(p),
                                    }))}
                                    activeKey={selectedPhaseId}
                                    onChange={(key) => {
                                        const phase = phaseTabs.find((p) => String(p.id) === String(key));
                                        if (phase) setSelectedPhaseId(phase.id);
                                    }}
                                    fitContent
                                />
                            </Container>
                        ) : (
                            <MutedText style={{ fontSize: 14, marginTop: 16 }}>No phases yet. Add a phase when available.</MutedText>
                        )}

                        {selectedPhase && (
                            <Container style={{ marginBottom: 24, marginTop: 8 }}>
                                <Container
                                    $display="flex"
                                    $alignItems="flex-start"
                                    $justifyContent="space-between"
                                    $gap="12px"
                                    $flexWrap="wrap"
                                    style={{ marginBottom: 24 }}
                                >
                                    <div>
                                        <PanelSectionLabel style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                            <AppstoreOutlined />
                                            Phase Title
                                        </PanelSectionLabel>
                                        <BoardPageTitle style={{ fontSize: 20, margin: 0 }}>{getPhaseDisplayTitle(selectedPhase)}</BoardPageTitle>
                                    </div>
                                    {!selectedPhase._synthetic && (
                                        <MultipleButton
                                            leftLabel="Edit"
                                            rightLabel="Delete"
                                            leftIcon={<EditOutlined />}
                                            rightIcon={<DeleteOutlined />}
                                            onLeftClick={canEditPhase ? () => openEditPhaseModal(selectedPhase) : undefined}
                                            onRightClick={canDeletePhase && !deletePhasePending ? onDeletePhase : undefined}
                                            leftPermissionDenied={!canEditPhase}
                                            rightPermissionDenied={!canDeletePhase}
                                            $size="12px"
                                            $padding="0 12px"
                                            $minHeight="28px"
                                            $entryAnimation={false}
                                            $autoWidth
                                            style={{ height: 28, minHeight: 28, borderRadius: 8 }}
                                            isHidden={role === 'Client'}
                                        />
                                    )}
                                </Container>
                                {hasPhaseDescriptionBody ? (
                                    <div>
                                        <PanelSectionLabel style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                            <AlignLeftOutlined />
                                            Description
                                        </PanelSectionLabel>
                                        {phaseDescriptionHtml.trim() ? (
                                            <PhaseDescriptionCollapsible
                                                source={phaseDescriptionLinked}
                                                resetKey={selectedPhaseId}
                                                attachmentItems={getPhaseAttachmentList(selectedPhase)}
                                                alwaysExpanded={!useMilestones || !milestonesForPhase || milestonesForPhase.length === 0}
                                                onLinkClick={onPhaseLinkClick}
                                            />
                                        ) : null}
                                        <PhaseDescriptionAttachments items={phaseAttachments} />
                                    </div>
                                ) : (
                                    <div>
                                        <PanelSectionLabel style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                            <AlignLeftOutlined />
                                            Description
                                        </PanelSectionLabel>
                                        <MutedText style={{ fontSize: 14, margin: 0 }}>No description for this phase.</MutedText>
                                    </div>
                                )}
                            </Container>
                        )}
                    </>
                )}

                {usePhases && useMilestones && <Divider style={{ margin: '28px 0' }} />}

                {/* Milestones */}
                {useMilestones && (
                    <>
                        <Container
                            $display="flex"
                            $justifyContent="space-between"
                            $alignItems="flex-start"
                            $gap="16px"
                            $flexWrap="wrap"
                            style={{ flexShrink: 0 }}
                        >
                            <div>
                                <BoardPageTitle>Milestones</BoardPageTitle>
                                <BoardPageSubtitle>View milestones, payment release, and deliverables for this project.</BoardPageSubtitle>
                            </div>
                            <Button
                                type="button"
                                $primary
                                $blackOnLight
                                $size="13px"
                                $padding="8px 16px"
                                icon={<PlusOutlined />}
                                permissionDenied={!canCreateMilestone}
                                disabled={createMilestoneDisabled}
                                disabledTooltip={
                                    canCreateMilestone && createMilestoneDisabled ? createMilestoneDisabledTooltip : undefined
                                }
                                onClick={openMilestoneModal}
                                isHidden={role === 'Client'}
                            >
                                Create Milestone
                            </Button>
                        </Container>

                        {milestonesForPhase.length === 0 ? (
                            <EmptyStateBlock style={{ marginTop: 24 }}>
                                <EmptyStateIcon>
                                    <InboxOutlined />
                                </EmptyStateIcon>
                                <EmptyStateTitle>No milestones for this selection</EmptyStateTitle>
                                <EmptyStateDescription>Create a milestone or choose another phase.</EmptyStateDescription>
                            </EmptyStateBlock>
                        ) : (
                            <>
                                <Container
                                    $display="flex"
                                    $alignItems="flex-end"
                                    $gap="12px"
                                    $flexWrap="nowrap"
                                    style={{ marginTop: 20, marginBottom: 20, flexShrink: 0, minWidth: 0 }}
                                >
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <ScrollWrapper>
                                            <style>{`.milestone-scroll-inner::-webkit-scrollbar { display: none; }`}</style>
                                            {showLeftScroll && (
                                                <ScrollArrow type="button" $left onClick={() => scrollBy(-200)}>
                                                    <LeftOutlined />
                                                </ScrollArrow>
                                            )}
                                            <BoardUnderlineTabRow style={{ marginTop: 0, marginBottom: 0 }}>
                                                <BoardUnderlineTabInner
                                                    className="milestone-scroll-inner"
                                                    ref={trackRef}
                                                    onScroll={updateScrollButtons}
                                                    style={{ scrollbarWidth: 'none' }}
                                                >
                                                    {milestonesForPhase.map((m) => {
                                                        const active = String(selectedMilestoneId) === String(m.id);
                                                        const pct = getMilestoneDeliverableCompletionPercent(m);
                                                        const label = getMilestoneDisplayLabel(m);
                                                        return (
                                                            <BoardUnderlineTab
                                                                key={m.id}
                                                                type="button"
                                                                $active={active}
                                                                onClick={() => setSelectedMilestoneId(m.id)}
                                                                title={`${label} ${pct}%`}
                                                                style={{ height: 'auto', paddingBottom: 8 }}
                                                            >
                                                                <Container $display="flex" $flexDirection="column" $width="100%" $alignItems="stretch" $gap="4px">
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', gap: '8px' }}>
                                                                        <span
                                                                            style={{
                                                                                fontSize: 12,
                                                                                fontWeight: 500,
                                                                                color: 'var(--ant-color-text-description, #8c8c8c)',
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis',
                                                                                whiteSpace: 'nowrap'
                                                                            }}
                                                                        >
                                                                            {label}
                                                                        </span>
                                                                        <span
                                                                            style={{
                                                                                fontSize: 12,
                                                                                fontWeight: 600,
                                                                                color: active ? 'var(--ant-color-primary, #7543DC)' : 'var(--ant-color-text-description, #8c8c8c)'
                                                                            }}
                                                                        >
                                                                            {pct}%
                                                                        </span>
                                                                    </div>
                                                                    <div
                                                                        style={{
                                                                            fontSize: 14,
                                                                            fontWeight: 600,
                                                                            textAlign: 'left',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap',
                                                                            width: '100%',
                                                                            color: active ? 'var(--ant-color-text, #171717)' : 'var(--ant-color-text-secondary, #737373)',
                                                                        }}
                                                                        title={m.name || ''}
                                                                    >
                                                                        {m.name || 'Untitled'}
                                                                    </div>
                                                                </Container>
                                                            </BoardUnderlineTab>
                                                        );
                                                    })}
                                                </BoardUnderlineTabInner>
                                            </BoardUnderlineTabRow>
                                            {showRightScroll && (
                                                <ScrollArrow type="button" $right onClick={() => scrollBy(200)}>
                                                    <RightOutlined />
                                                </ScrollArrow>
                                            )}
                                        </ScrollWrapper>
                                    </div>
                                </Container>

                                {selectedMilestone && (
                                    <Container $display="flex" $flexDirection="column" $gap="24px" style={{ marginBottom: 20 }}>
                                        <Container $display="flex" $justifyContent="space-between" $alignItems="flex-start" $gap="16px" $flexWrap="wrap">
                                                <div style={{ minWidth: 0 }}>
                                                    <PanelSectionLabel style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                        <CalendarOutlined />
                                                        Milestone Title
                                                    </PanelSectionLabel>
                                                    <BoardPageTitle style={{ fontSize: 20, margin: 0 }}>
                                                        {selectedMilestone.name}
                                                    </BoardPageTitle>
                                                </div>
                                                <Container $display="flex" $alignItems="center" $gap="12px">
                                                    <StatusBadge 
                                                        status={selectedMilestone.status} 
                                                        style={{ height: 28, borderRadius: 8, padding: '0 12px', display: 'flex', alignItems: 'center', boxSizing: 'border-box' }} 
                                                    />
                                                    {selectedMilestone && (
                                                        <MultipleButton
                                                            leftLabel="Edit"
                                                            rightLabel="Delete"
                                                            leftIcon={<EditOutlined />}
                                                            rightIcon={<DeleteOutlined />}
                                                            onLeftClick={canEditMilestone ? openEditMilestoneModal : undefined}
                                                            onRightClick={canDeleteMilestone && !deleteMilestonePending ? onDeleteMilestone : undefined}
                                                            leftPermissionDenied={!canEditMilestone}
                                                            rightPermissionDenied={!canDeleteMilestone}
                                                            $size="12px"
                                                            $padding="0 12px"
                                                            $minHeight="28px"
                                                            $entryAnimation={false}
                                                            $autoWidth
                                                            style={{ height: 28, minHeight: 28, borderRadius: 8 }}
                                                            isHidden={role === 'Client'}
                                                        />
                                                    )}
                                                </Container>
                                            </Container>

                                            <div>
                                                <PanelSectionLabel style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                                    <AlignLeftOutlined />
                                                    Description
                                                </PanelSectionLabel>
                                                <ClampedMarkdownDescription
                                                    source={linkifyPlainUrlsInHtml(
                                                        resolveDescriptionMentionsForDisplay(
                                                            linkifyPlainUrlsForMarkdown(getMilestoneDescriptionText(selectedMilestone)),
                                                            selectedMilestone?.descriptionMentions,
                                                        ),
                                                    )}
                                                    lineClamp={4}
                                                    stepLineClamps={[4, 9, null]}
                                                    resetKey={selectedMilestone.id}
                                                    markdownProps={{
                                                        variant: 'compact',
                                                        linkPrimary: true,
                                                        richInlineAnchors: true,
                                                        attachmentItems: milestoneAttachments,
                                                        onLinkClick: onMilestoneLinkClick,
                                                        emptyFallback: (
                                                            <MutedText style={{ margin: 0 }}>No description for this milestone.</MutedText>
                                                        ),
                                                    }}
                                                />

                                                <PhaseDescriptionAttachments
                                                    items={milestoneAttachments}
                                                />
                                            </div>

                                            <MilestoneMetaRow
                                                dueDate={selectedMilestone.dueDate ? formatDate(selectedMilestone.dueDate) : null}
                                                deliverableCount={milestoneDeliverables.length}
                                            />

                                            {canViewPayments && (
                                                <div>
                                                    <PanelSectionLabel style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                                    <DollarOutlined />
                                                    Payment release
                                                </PanelSectionLabel>
                                                <ContentCard $padding="0" $marginBottom="0">
                                                    <ContentCardRow>
                                                        <MutedText style={{ margin: 0, fontSize: 14 }}>Amount</MutedText>
                                                        <strong style={{ fontSize: 15 }}>{budget != null ? asCurrency(budget) : '—'}</strong>
                                                    </ContentCardRow>
                                                    <ContentCardRow>
                                                        <MutedText style={{ margin: 0, fontSize: 14 }}>Status</MutedText>
                                                        {paymentStatus ? (
                                                            <StatusBadge status={paymentStatus} />
                                                        ) : (
                                                            <MutedText style={{ margin: 0 }}>—</MutedText>
                                                        )}
                                                    </ContentCardRow>
                                                </ContentCard>
                                            </div>
                                            )}

                                            <MilestoneDeliverablesPanel
                                                milestoneId={selectedMilestone.id}
                                                deliverables={milestoneDeliverables}
                                                canEditMilestone={canEditMilestone}
                                                onAddDeliverable={onAddMilestoneDeliverable}
                                                onToggleDeliverable={onToggleMilestoneDeliverable}
                                                onEditDeliverable={onEditMilestoneDeliverable}
                                                onDeleteDeliverable={onDeleteMilestoneDeliverable}
                                                addPending={createDeliverablePending}
                                                togglePending={updateDeliverablePending}
                                                editPending={editDeliverablePending}
                                                deletePending={deleteDeliverablePending}
                                            />
                                        </Container>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        </Container>
    );
}
